"use client"

/**
 * Audio Scripter
 * A dedicated script editor for audiochan audio tracks.
 * Uses the SIB player as the master clock — no video needed.
 * Shows the audio waveform, a moving playhead, and a block-based script editor.
 */

import { useState, useEffect, useRef, useCallback } from "react"
import { useSibStore } from "@/lib/sib-store"
import { useScriptStore, BUILTIN_PATTERNS, type ScriptBlock, type BlockTransition } from "@/lib/script-store"
import { useEditorPlayback } from "@/hooks/use-editor-playback"
import { actionGenerator } from "@/lib/action-generator"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Slider } from "@/components/ui/slider"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent } from "@/components/ui/card"
import { cn } from "@/lib/utils"
import {
  Play, Pause, Square, SkipBack, SkipForward, Save, X,
  Plus, Trash2, Copy, Music, ChevronDown, ChevronUp,
} from "@/lib/icons"
import type { FunscriptAction } from "@/lib/script-store"
import { useTranslations } from "next-intl"

interface AudioScripterProps {
  audioSourceId: string        // e.g. "audiochan:uuid"
  audioTitle: string
  audioArtist: string
  audioDuration: number        // seconds
  waveformData?: number[]      // 0-1 values from audiochan API
  onClose: () => void
}

interface TimelineBlock extends ScriptBlock {
  pattern: (typeof BUILTIN_PATTERNS)[0]
}

function formatTime(seconds: number): string {
  if (!isFinite(seconds) || seconds < 0) return "0:00"
  const m = Math.floor(seconds / 60)
  const s = Math.floor(seconds % 60)
  return `${m}:${s.toString().padStart(2, "0")}`
}

// ── Waveform component ────────────────────────────────────────────────────────
function AudioWaveform({
  waveform,
  duration,
  currentTime,
  onSeek,
}: {
  waveform: number[]
  duration: number
  currentTime: number
  onSeek: (time: number) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const progress = duration > 0 ? currentTime / duration : 0

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      const rect = e.currentTarget.getBoundingClientRect()
      const x = e.clientX - rect.left
      const ratio = x / rect.width
      onSeek(ratio * duration)
    },
    [duration, onSeek],
  )

  // Generate bars from waveform data or fallback to flat line
  const bars = waveform.length > 0 ? waveform : Array(100).fill(0.3)
  const playedBars = Math.floor(progress * bars.length)

  return (
    <div
      ref={containerRef}
      className="relative w-full h-20 cursor-pointer select-none group"
      onClick={handleClick}
    >
      {/* Bars */}
      <div className="absolute inset-0 flex items-end gap-px px-1">
        {bars.map((h, i) => (
          <div
            key={i}
            className="flex-1 rounded-t-sm min-w-0 transition-colors"
            style={{
              height: `${Math.max(4, h * 100)}%`,
              background: i < playedBars
                ? `rgba(139,92,246,${0.6 + h * 0.4})`
                : `rgba(139,92,246,${0.15 + h * 0.15})`,
            }}
          />
        ))}
      </div>

      {/* Playhead */}
      <div
        className="absolute top-0 bottom-0 w-0.5 bg-white/80 shadow-[0_0_6px_rgba(255,255,255,0.6)] pointer-events-none transition-none"
        style={{ left: `${progress * 100}%` }}
      />

      {/* Time labels */}
      <div className="absolute bottom-0 left-1 text-[10px] text-white/30 pointer-events-none">
        {formatTime(currentTime)}
      </div>
      <div className="absolute bottom-0 right-1 text-[10px] text-white/30 pointer-events-none">
        {formatTime(duration)}
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────
export function AudioScripter({
  audioSourceId,
  audioTitle,
  audioArtist,
  audioDuration,
  waveformData = [],
  onClose,
}: AudioScripterProps) {
  const tt = useTranslations("audioScripter")
  const {
    isPlaying: sibPlaying,
    currentTime: sibTime,
    currentTrack,
    pause: sibPause,
    resume: sibResume,
    setCurrentTime: sibSetTime,
  } = useSibStore()

  const { customPatterns, addScript } = useScriptStore()
  const { play: devicePlay, stop: deviceStop, globalPlayback } = useEditorPlayback({ source: "audio-scripter" })
  const devicePlaying = globalPlayback.isPlaying

  const allPatterns = [...BUILTIN_PATTERNS, ...customPatterns]

  const [scriptName, setScriptName] = useState(`Script for ${audioTitle}`)
  const [blocks, setBlocks] = useState<TimelineBlock[]>([])
  const [transitions, setTransitions] = useState<BlockTransition[]>([])
  const [selectedBlockIndex, setSelectedBlockIndex] = useState<number | null>(null)
  const [isPreviewingDevice, setIsPreviewingDevice] = useState(false)

  const duration = audioDuration || currentTrack?.duration || 0

  // ── Smooth 60fps display time ────────────────────────────────────────────────
  // SIB timeupdate fires ~4x/sec. We interpolate locally using Date.now() so
  // the waveform playhead moves smoothly at 60fps.
  const [displayTime, setDisplayTime] = useState(sibTime)
  const rafRef = useRef<number | null>(null)
  const playStartRef = useRef<number>(0) // Date.now() when sibPlaying became true

  useEffect(() => {
    if (!sibPlaying) {
      // When paused, snap display to SIB's reported time
      setDisplayTime(sibTime)
      if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null }
      return
    }
    // Record when playback started relative to current sibTime
    playStartRef.current = Date.now() - sibTime * 1000
    const tick = () => {
      const elapsed = (Date.now() - playStartRef.current) / 1000 // seconds
      setDisplayTime(Math.min(elapsed, duration))
      rafRef.current = requestAnimationFrame(tick)
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => { if (rafRef.current) { cancelAnimationFrame(rafRef.current); rafRef.current = null } }
  }, [sibPlaying, sibTime, duration])

  // Seek SIB audio
  const handleSeek = useCallback(
    (time: number) => {
      sibSetTime(time)
      // Also seek the actual audio element via SIB store
      useSibStore.setState({ currentTime: time })
    },
    [sibSetTime],
  )

  // Block management
  const addBlock = useCallback(
    (patternId: string) => {
      const pattern = allPatterns.find((p) => p.id === patternId)
      if (!pattern) return
      const newBlock: TimelineBlock = {
        id: `block-${Date.now()}`,
        patternId,
        duration: 10,
        repeatCount: 1,
        speedMultiplier: 1,
        intensityMultiplier: 1,
        strokeLengthMultiplier: 1,
        pattern,
      }
      setBlocks((prev) => [...prev, newBlock])
      if (blocks.length > 0) {
        setTransitions((prev) => [...prev, { type: "fade", duration: 500 }])
      }
      setSelectedBlockIndex(blocks.length)
    },
    [allPatterns, blocks.length],
  )

  const updateBlock = useCallback((index: number, updates: Partial<TimelineBlock>) => {
    setBlocks((prev) => prev.map((b, i) => (i === index ? { ...b, ...updates } : b)))
  }, [])

  const removeBlock = useCallback((index: number) => {
    setBlocks((prev) => prev.filter((_, i) => i !== index))
    setTransitions((prev) => prev.filter((_, i) => i !== Math.max(0, index - 1)))
    setSelectedBlockIndex((prev) => {
      if (prev === null) return null
      if (prev === index) return null
      return prev > index ? prev - 1 : prev
    })
  }, [])

  const duplicateBlock = useCallback((index: number) => {
    setBlocks((prev) => {
      const copy = { ...prev[index], id: `block-${Date.now()}` }
      const next = [...prev]
      next.splice(index + 1, 0, copy)
      return next
    })
    setTransitions((prev) => [...prev, { type: "fade", duration: 500 }])
  }, [])

  const getTotalDuration = () =>
    blocks.reduce((t, b) => t + b.duration * b.repeatCount, 0)

  const generateFunscript = () => {
    const actions = actionGenerator.fromPatternBlocks(blocks)
    return {
      version: "1.0",
      inverted: false,
      range: 100,
      actions,
      metadata: {
        title: scriptName,
        creator: "Audio Scripter",
        duration: getTotalDuration() * 1000,
      },
    }
  }

  // Device preview
  const handleDevicePreview = async () => {
    if (isPreviewingDevice) {
      deviceStop()
      setIsPreviewingDevice(false)
    } else {
      const fs = generateFunscript()
      if (fs.actions.length > 0) {
        await devicePlay({
          id: "audio-scripter-preview",
          name: scriptName,
          funscript: fs,
        } as any)
        setIsPreviewingDevice(true)
      }
    }
  }

  useEffect(() => {
    if (!devicePlaying && isPreviewingDevice) setIsPreviewingDevice(false)
  }, [devicePlaying, isPreviewingDevice])

  // Timeline bar visualization for a block
  const blockTimelineWidth = (block: TimelineBlock) => {
    if (duration <= 0) return 0
    return Math.min(100, ((block.duration * block.repeatCount) / duration) * 100)
  }

  const blockTimelineOffset = (index: number) => {
    if (duration <= 0) return 0
    const elapsed = blocks.slice(0, index).reduce((t, b) => t + b.duration * b.repeatCount, 0)
    return Math.min(100, (elapsed / duration) * 100)
  }

  return (
    <div className="fixed inset-0 z-50 bg-[#0d0d14] flex flex-col">

      {/* ── Header ── */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-white/[0.07] shrink-0">
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <Music className="h-4 w-4 text-purple-400 shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-white truncate">{audioTitle}</p>
            <p className="text-[11px] text-white/40">{audioArtist}</p>
          </div>
        </div>
        <Input
          value={scriptName}
          onChange={(e) => setScriptName(e.target.value)}
          className="w-48 h-8 text-sm bg-white/[0.05] border-white/10 text-white"
          placeholder={tt("scriptName")}
        />
        <Badge variant="outline" className="text-white/50 border-white/10 shrink-0">
          {tt("valueS", { value: getTotalDuration() })}
        </Badge>
        <Button size="sm" variant="outline"
          className="border-white/10 text-white/70 hover:text-white bg-transparent"
          onClick={handleDevicePreview}>
          {isPreviewingDevice ? <><Square className="h-3.5 w-3.5 mr-1.5" />{tt("stop")}</> : <><Play className="h-3.5 w-3.5 mr-1.5" />{tt("preview")}</>}
        </Button>
        <Button size="sm" className="bg-purple-600 hover:bg-purple-500 text-white"
          onClick={() => {
            const funscript = generateFunscript()
            addScript({
              name: scriptName,
              description: "",
              funscript,
              tags: [],
              deviceCompatibility: [],
              averageIntensity: 50,
              isPublished: false,
              audioSourceId,
              audioSourceTitle: audioTitle,
              contentType: "audio",
              editorMetadata: { editorType: "story-scripter" },
            })
            import("sonner").then(({ toast }) => toast.success(tt("scriptSavedToMyScripts")))
          }}>
          <Save className="h-3.5 w-3.5 mr-1.5" />{tt("save")}
        </Button>
        <Button variant="ghost" size="icon" className="h-8 w-8 text-white/40 hover:text-white" onClick={onClose}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* ── Waveform + transport ── */}
      <div className="px-4 py-3 border-b border-white/[0.07] bg-[#111118] shrink-0">
        <AudioWaveform
          waveform={waveformData}
          duration={duration}
          currentTime={displayTime}
          onSeek={handleSeek}
        />
        {/* Transport controls */}
        <div className="flex items-center justify-center gap-2 mt-2">
          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50 hover:text-white"
            onClick={() => handleSeek(Math.max(0, sibTime - 10))}>
            <SkipBack className="h-3.5 w-3.5" />
          </Button>
          <Button variant="ghost" size="icon" className="h-8 w-8 text-white hover:text-purple-300"
            onClick={() => sibPlaying ? sibPause() : sibResume()}>
            {sibPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </Button>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-white/50 hover:text-white"
            onClick={() => handleSeek(Math.min(duration, sibTime + 10))}>
            <SkipForward className="h-3.5 w-3.5" />
          </Button>
          <span className="text-[11px] text-white/40 tabular-nums ml-2">
            {formatTime(displayTime)} / {formatTime(duration)}
          </span>
          {/* "Set block start here" button */}
          {selectedBlockIndex !== null && (
            <Button size="sm" variant="outline"
              className="ml-4 h-7 text-[11px] border-purple-600/50 text-purple-300 hover:bg-purple-900/30 bg-transparent"
              onClick={() => {
                // Store current time as a note — blocks are sequential so this is informational
                // In a future version this would set the block's absolute start time
              }}
              title={tt("currentPlayheadPosition")}>
              ⏱ {formatTime(displayTime)}
            </Button>
          )}
        </div>

        {/* Timeline bar — shows blocks mapped to audio duration */}
        {blocks.length > 0 && duration > 0 && (
          <div className="relative h-4 mt-3 bg-white/[0.04] rounded overflow-hidden">
            {blocks.map((block, i) => (
              <div
                key={block.id}
                className={cn(
                  "absolute top-0 bottom-0 rounded cursor-pointer transition-opacity",
                  selectedBlockIndex === i ? "opacity-100" : "opacity-60 hover:opacity-80",
                )}
                style={{
                  left: `${blockTimelineOffset(i)}%`,
                  width: `${blockTimelineWidth(block)}%`,
                  background: `hsl(${(i * 47) % 360}, 60%, 55%)`,
                }}
                onClick={() => setSelectedBlockIndex(i)}
                title={`Block ${i + 1}: ${block.pattern.name}`}
              />
            ))}
            {/* Playhead on timeline bar */}
            <div
              className="absolute top-0 bottom-0 w-0.5 bg-white/80 pointer-events-none"
              style={{ left: `${duration > 0 ? (displayTime / duration) * 100 : 0}%` }}
            />
          </div>
        )}
      </div>

      {/* ── Main area: pattern sidebar + block list ── */}
      <div className="flex-1 min-h-0 flex overflow-hidden">

        {/* Pattern sidebar */}
        <div className="w-44 border-r border-white/[0.07] flex flex-col shrink-0 bg-[#0f0f18]">
          <p className="text-[10px] font-semibold text-white/30 uppercase tracking-wider px-3 pt-3 pb-1">
            {tt("patterns")}
          </p>
          <div className="flex-1 overflow-y-auto px-2 pb-3 space-y-0.5">
            {allPatterns.map((pattern) => (
              <button
                key={pattern.id}
                className="w-full text-left px-2 py-1.5 rounded-md hover:bg-purple-900/30 hover:text-purple-200 transition-colors text-white/60"
                onClick={() => addBlock(pattern.id)}
              >
                <p className="text-[11px] font-medium truncate">{pattern.name}</p>
                <p className="text-[10px] text-white/30">{tt("intensityIntensity", { intensity: pattern.intensity })}</p>
              </button>
            ))}
          </div>
        </div>

        {/* Block list */}
        <div className="flex-1 min-w-0 overflow-y-auto p-3 space-y-2"
          style={{ paddingBottom: "calc(0.75rem + var(--sib-player-height, 0px))" }}>
          {blocks.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-full text-center py-16">
              <Music className="h-8 w-8 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">{tt("noBlocksYet")}</p>
              <p className="text-white/20 text-xs mt-1">{tt("clickAPatternOnTheLeftToAddABlock")}</p>
            </div>
          ) : (
            blocks.map((block, index) => (
              <div
                key={block.id}
                className={cn(
                  "rounded-lg border transition-all",
                  selectedBlockIndex === index
                    ? "border-purple-600/60 bg-purple-950/30"
                    : "border-white/[0.07] bg-white/[0.02] hover:border-white/[0.12]",
                )}
              >
                {/* Block header */}
                <div
                  className="flex items-center gap-2 px-3 py-2 cursor-pointer"
                  onClick={() => setSelectedBlockIndex(selectedBlockIndex === index ? null : index)}
                >
                  <div
                    className="h-3 w-3 rounded-sm shrink-0"
                    style={{ background: `hsl(${(index * 47) % 360}, 60%, 55%)` }}
                  />
                  <span className="text-[12px] font-medium text-white/80 flex-1 truncate">
                    {block.pattern.name}
                  </span>
                  <span className="text-[10px] text-white/30 shrink-0">
                    {tt("durationSRepeatCountValueS", { duration: block.duration, repeatCount: block.repeatCount, value: block.duration * block.repeatCount })}
                  </span>
                  <Button variant="ghost" size="icon" className="h-5 w-5 text-white/30 hover:text-white"
                    onClick={(e) => { e.stopPropagation(); duplicateBlock(index) }}>
                    <Copy className="h-3 w-3" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-5 w-5 text-white/30 hover:text-red-400"
                    onClick={(e) => { e.stopPropagation(); removeBlock(index) }}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                  {selectedBlockIndex === index
                    ? <ChevronUp className="h-3.5 w-3.5 text-white/30" />
                    : <ChevronDown className="h-3.5 w-3.5 text-white/30" />
                  }
                </div>

                {/* Block settings — expanded */}
                {selectedBlockIndex === index && (
                  <div className="px-3 pb-3 pt-1 border-t border-white/[0.06] grid grid-cols-2 gap-3"
                    onClick={(e) => e.stopPropagation()}>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-white/40">{tt("durationDurationS", { duration: block.duration })}</Label>
                      <Slider value={[block.duration]}
                        onValueChange={([v]) => updateBlock(index, { duration: v })}
                        min={1} max={120} step={1} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-white/40">{tt("repeatRepeatCount", { repeatCount: block.repeatCount })}</Label>
                      <Slider value={[block.repeatCount]}
                        onValueChange={([v]) => updateBlock(index, { repeatCount: v })}
                        min={1} max={20} step={1} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-white/40">{tt("speedValue", { value: Math.round(block.speedMultiplier * 100) })}</Label>
                      <Slider value={[block.speedMultiplier * 100]}
                        onValueChange={([v]) => updateBlock(index, { speedMultiplier: v / 100 })}
                        min={25} max={200} step={5} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-[10px] text-white/40">{tt("intensityValue", { value: Math.round(block.intensityMultiplier * 100) })}</Label>
                      <Slider value={[block.intensityMultiplier * 100]}
                        onValueChange={([v]) => updateBlock(index, { intensityMultiplier: v / 100 })}
                        min={25} max={200} step={5} />
                    </div>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  )
}
